// The shell's layout state (UAT F-146, Tejesh 12 Oct 05:54): the navigation's width and its icon-rail fold, and the
// assistant panel's open state and width, remembered per browser. Everything here is pure, so the rules are tested
// without a browser (tests/shell/shell-layout.test.mjs); shell/MissionShell.tsx holds the state and draws it.
//
//   Navigation: 260 px by default (DS §1.3), dragged between 180 and 320 px by the handle on its right edge; dragged below
//     180 px it snaps to DC-38's 76 px icon rail, and dragged back past 180 px (or the chevron on its edge, or Enter on the handle)
//     it opens again at the width it had. Until the person chooses, it folds to the rail at ≤ 1180 px (DC-38) and is
//     open above; once they choose, their choice holds at every width. A phone (≤ 768 px) keeps its drawer.
//   Assistant: closed by default, 360 px when open, dragged between 320 and 560 px by the handle on its left edge. It
//     docks beside the detail pane while the pane keeps at least MAIN_MIN px; where it cannot (a phone, or a narrow
//     window with the navigation open) it lies over the pane as a sheet instead, so nothing scrolls sideways.
//   Memory: one JSON value under ta.shell.layout, read and written inside try/catch (storage can be unavailable, and
//     the shell then draws the defaults), never under capture (ground rule 8: a photographed frame starts at the
//     defaults). Each field is checked on the way in, so a stale or hand-edited value falls back field by field.

/** The navigation's widths in px: the default, the snap (the least open width), the most, the rail and a key step. */
export const NAV = Object.freeze({ initial: 260, min: 180, max: 320, rail: 76, step: 16 });
/** The assistant panel's widths in px. */
export const ASIDE = Object.freeze({ initial: 360, min: 320, max: 560, step: 16 });
/**
 * The least the detail pane keeps beside a docked panel; where it cannot, the panel becomes a sheet. 560 px is the
 * narrowest pane the F-146 sweep found every screen whole at, at desktop widths (lists, boards, the requisition, the
 * candidate profile, the application, intake; the requisition's compensation rows need about 470 px).
 */
export const MAIN_MIN = 560;
/** DC-38: the navigation folds to the rail at ≤ 1180 px until the person chooses; the phone's drawer is ≤ 768 px. */
export const RAIL_MAX = 1180;
export const MOBILE_MAX = 768;
/** The key the layout is remembered under (beside ta.theme and ta.theme.seat). */
export const LAYOUT_KEY = "ta.shell.layout";

export interface NavLayout {
  /** The open width, 180–320 px; kept while folded, so the navigation opens again where it was. */
  width: number;
  /** The person's choice; null until they make one (then the rail follows the viewport, DC-38). */
  collapsed: boolean | null;
}

export interface AsideLayout {
  open: boolean;
  /** 320–560 px. */
  width: number;
}

export interface ShellLayout {
  nav: NavLayout;
  aside: AsideLayout;
}

export const DEFAULT_LAYOUT: ShellLayout = Object.freeze({
  nav: Object.freeze({ width: NAV.initial, collapsed: null }),
  aside: Object.freeze({ open: false, width: ASIDE.initial }),
}) as ShellLayout;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(value)));

/** An open navigation width held to 180–320 px. */
export function clampNav(width: number): number {
  return clamp(width, NAV.min, NAV.max);
}

/** An assistant width held to 320–560 px. */
export function clampAside(width: number): number {
  return clamp(width, ASIDE.min, ASIDE.max);
}

/** Whether the navigation draws as the icon rail: the person's choice, else DC-38's fold at ≤ 1180 px. */
export function navFolded(nav: NavLayout, viewport: number): boolean {
  return nav.collapsed ?? viewport <= RAIL_MAX;
}

/**
 * The navigation after its handle is dragged to `pointer` px from its left edge: below the snap width it folds to the
 * rail (keeping the width it had), at or past it it is open at that width, held to 180–320 px.
 */
export function navDrag(nav: NavLayout, pointer: number): NavLayout {
  if (!Number.isFinite(pointer)) return nav;
  if (pointer < NAV.min) return { width: nav.width, collapsed: true };
  return { width: clampNav(pointer), collapsed: false };
}

/** The navigation's chevron (and Enter on the handle): folds an open navigation, opens a folded one at its width. */
export function navToggle(nav: NavLayout, viewport: number): NavLayout {
  return { width: nav.width, collapsed: !navFolded(nav, viewport) };
}

/**
 * The navigation handle's keys (the window-splitter pattern): ← narrows by a step and folds below the snap width,
 * → widens by a step (opening a folded navigation at the snap width or its own), Home folds it, End opens it at the
 * most, Enter toggles. Returns null for any other key.
 */
export function navKey(nav: NavLayout, key: string, viewport: number): NavLayout | null {
  const folded = navFolded(nav, viewport);
  switch (key) {
    case "ArrowLeft":
      return folded ? { ...nav, collapsed: true } : navDrag(nav, nav.width - NAV.step);
    case "ArrowRight":
      return folded ? { width: clampNav(nav.width), collapsed: false } : { width: clampNav(nav.width + NAV.step), collapsed: false };
    case "Home":
      return { width: nav.width, collapsed: true };
    case "End":
      return { width: NAV.max, collapsed: false };
    case "Enter":
      return navToggle(nav, viewport);
    default:
      return null;
  }
}

/** The assistant after its handle (on the panel's left edge) is dragged to `width` px from the window's right edge. */
export function asideDrag(aside: AsideLayout, width: number): AsideLayout {
  if (!Number.isFinite(width)) return aside;
  return { ...aside, width: clampAside(width) };
}

/**
 * The assistant handle's keys: the handle is the panel's left edge, so ← widens the panel by a step and → narrows it;
 * Home gives it the least width and End the most. Returns null for any other key.
 */
export function asideKey(aside: AsideLayout, key: string): AsideLayout | null {
  switch (key) {
    case "ArrowLeft":
      return asideDrag(aside, aside.width + ASIDE.step);
    case "ArrowRight":
      return asideDrag(aside, aside.width - ASIDE.step);
    case "Home":
      return { ...aside, width: ASIDE.min };
    case "End":
      return { ...aside, width: ASIDE.max };
    default:
      return null;
  }
}

export interface FrameLayout {
  /** The navigation's column in px (the rail's 76 when folded); a phone's drawer takes no column. */
  navWidth: number;
  navFolded: boolean;
  /** closed; dock (a column beside the detail pane); sheet (over the pane, on a phone or a narrow window). */
  aside: "closed" | "dock" | "sheet";
  /** The panel's drawn width in px (0 when closed). */
  asideWidth: number;
  /** The most a drag may widen the panel to at this viewport. */
  asideMax: number;
}

/**
 * What the frame draws at a viewport width: the navigation's column, and whether the open panel docks or lies over
 * the pane as a sheet. A docked panel gives up width (never below 320 px) before the detail pane drops under
 * MAIN_MIN; where even 320 px would, it is a sheet at its own width (the viewport's on a phone).
 */
export function frameLayout(layout: ShellLayout, viewport: number): FrameLayout {
  const mobile = viewport <= MOBILE_MAX;
  const folded = !mobile && navFolded(layout.nav, viewport);
  const navWidth = mobile ? 0 : folded ? NAV.rail : clampNav(layout.nav.width);
  const room = viewport - navWidth - MAIN_MIN;
  const asideMax = Math.max(ASIDE.min, Math.min(ASIDE.max, room));
  if (!layout.aside.open) return { navWidth, navFolded: folded, aside: "closed", asideWidth: 0, asideMax };
  const width = clampAside(layout.aside.width);
  if (mobile) return { navWidth, navFolded: folded, aside: "sheet", asideWidth: viewport, asideMax: viewport };
  if (room < ASIDE.min) return { navWidth, navFolded: folded, aside: "sheet", asideWidth: Math.min(width, viewport), asideMax };
  return { navWidth, navFolded: folded, aside: "dock", asideWidth: Math.min(width, asideMax), asideMax };
}

type LayoutStore = Pick<Storage, "getItem" | "setItem">;

function defaultStore(): LayoutStore | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** The remembered layout, each field checked and held to its bounds; the defaults where storage is absent or under capture. */
export function readShellLayout(capture: boolean, storage?: LayoutStore | null): ShellLayout {
  if (capture) return DEFAULT_LAYOUT;
  let raw: unknown = null;
  try {
    const text = (storage === undefined ? defaultStore() : storage)?.getItem(LAYOUT_KEY);
    raw = text ? JSON.parse(text) : null;
  } catch {
    return DEFAULT_LAYOUT;
  }
  if (!raw || typeof raw !== "object") return DEFAULT_LAYOUT;
  const { nav, aside } = raw as { nav?: Partial<NavLayout>; aside?: Partial<AsideLayout> };
  const number = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
  return {
    nav: {
      width: number(nav?.width) ? clampNav(nav.width) : NAV.initial,
      collapsed: typeof nav?.collapsed === "boolean" ? nav.collapsed : null,
    },
    aside: {
      open: aside?.open === true,
      width: number(aside?.width) ? clampAside(aside.width) : ASIDE.initial,
    },
  };
}

/** Remembers the layout for the next load; never under capture, and silently not where storage is unavailable. */
export function writeShellLayout(layout: ShellLayout, capture: boolean, storage?: LayoutStore | null): void {
  if (capture) return;
  try {
    (storage === undefined ? defaultStore() : storage)?.setItem(LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    // The layout still holds for this page.
  }
}

/** ⌘J or Ctrl+J, with no other modifier: open or close the assistant panel from anywhere. */
export function isAssistantShortcut(event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "j";
}

/** The shortcut as aria-keyshortcuts names it. */
export const ASSISTANT_KEYS = "Meta+J Control+J";

/** The shortcut as the tooltip shows it on this platform: ⌘J on an Apple device, Ctrl+J elsewhere. */
export function shortcutHint(platform: string | undefined): string {
  return /mac|iphone|ipad|ipod/i.test(platform ?? "") ? "⌘J" : "Ctrl+J";
}
